// src/pages/ordens-servico/NovaOS.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import { useIsMobile } from "../../hooks/useIsMobile";
import { uploadToStorage } from "../../utils/storageUpload";
import LayoutDesktop from "../../layouts/LayoutDesktop";

import { db } from "../../config/firebase";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  runTransaction,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

// contador dentro do grupo: os_<grupoId>
async function obterProximoCodigoSequencialOs(grupoId) {
  const contadorRef = doc(db, "contadores", `os_${grupoId}`);

  const proximo = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(contadorRef);

    let atual = 0;
    if (snap.exists() && snap.data().ultimoCodigoSequencial != null) {
      atual = snap.data().ultimoCodigoSequencial;
    }

    const novoValor = atual + 1;

    transaction.set(
      contadorRef,
      { ultimoCodigoSequencial: novoValor, grupoId },
      { merge: true }
    );

    return novoValor;
  });

  return proximo;
}

function NovaOS() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const { empresaAtual } = useEmpresa();
  const nivel = nivelUsuario(usuarioLogado);
  const podeVerTodas = (usuarioLogado?.acessoGlobal === true || usuarioLogado?.globalAccess === true || nivel >= NIVEIS.ADMIN);
  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = useIsMobile(900);
  const isKioskUser = nivel === NIVEIS.KIOSK;
  const podeAnexarFotoAbertura = isMobile && !isKioskUser;


  if (!usuarioLogado) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ opacity: 0.75 }}>Carregando...</div>
      </div>
    );
  }

  const [empresas, setEmpresas] = useState([]);
  const [setores, setSetores] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [statusList, setStatusList] = useState([]);

  const [carregandoEmpresas, setCarregandoEmpresas] = useState(false);
  const [carregandoSetores, setCarregandoSetores] = useState(false);
  const [carregandoMaquinas, setCarregandoMaquinas] = useState(false);
  const [carregandoStatus, setCarregandoStatus] = useState(false);

  // Empresa efetiva usada na OS (evita inconsistência entre empresa atual do usuário e seleção no form)
  const [empresaIdEfetivo, setEmpresaIdEfetivo] = useState("");
  const [setorId, setSetorId] = useState("");
  const [maquinaId, setMaquinaId] = useState("");

  // Mantém compatibilidade com o código antigo (que usava `empresaId`)
  // e evita ReferenceError (causa clássica de "tela preta" no Cliente).
  const empresaId = empresaIdEfetivo;

  const [statusId, setStatusId] = useState("");

  const [titulo, setTitulo] = useState("");
  const [defeitos, setDefeitos] = useState([]);
  const [carregandoDefeitos, setCarregandoDefeitos] = useState(false);
  const [defeitoId, setDefeitoId] = useState("");
  const [defeitoCustom, setDefeitoCustom] = useState("");
  const [descricao, setDescricao] = useState("");
  const [criticidade, setCriticidade] = useState("Média");
  const [maquinaParou, setMaquinaParou] = useState("Sim");
  const [tipoServico, setTipoServico] = useState("Corretiva");

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [salvando, setSalvando] = useState(false);

  // Fotos na abertura da OS (apenas mobile; não aparece para Kiosk)
  const [fotosAbertura, setFotosAbertura] = useState([]); // [{id, file, previewUrl, origem}]
  const [enviandoFotos, setEnviandoFotos] = useState(false);
  const inputFotoCameraRef = useRef(null);
  const inputFotoArquivoRef = useRef(null);
  const previewUrlsRef = useRef(new Set());

  function anexarFotosAbertura(fileList, origem) {
    const files = Array.from(fileList || []).filter(Boolean);
    const imagens = files.filter((f) => String(f.type || "").startsWith("image/"));
    if (imagens.length === 0) return;

    setFotosAbertura((prev) => {
      const next = [...prev];
      for (const file of imagens) {
        const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
        const previewUrl = URL.createObjectURL(file);
        previewUrlsRef.current.add(previewUrl);
        next.push({ id, file, previewUrl, origem: origem || "arquivo" });
      }
      return next;
    });
  }

  function removerFotoAbertura(id) {
    setFotosAbertura((prev) => {
      const item = prev.find((x) => x.id === id);
      if (item?.previewUrl) {
        try { URL.revokeObjectURL(item.previewUrl); } catch {}
        previewUrlsRef.current.delete(item.previewUrl);
      }
      return prev.filter((x) => x.id !== id);
    });
  }

  function limparFotosAbertura() {
    try {
      previewUrlsRef.current.forEach((url) => {
        try { URL.revokeObjectURL(url); } catch {}
      });
      previewUrlsRef.current.clear();
    } catch {}
    setFotosAbertura([]);
    if (inputFotoCameraRef.current) inputFotoCameraRef.current.value = "";
    if (inputFotoArquivoRef.current) inputFotoArquivoRef.current.value = "";
  }

  useEffect(() => {
    return () => {
      try {
        previewUrlsRef.current.forEach((url) => {
          try { URL.revokeObjectURL(url); } catch {}
        });
        previewUrlsRef.current.clear();
      } catch {}
    };
  }, []);

  // Pré-preenche campos quando vem de "Abrir OS" na tela de equipamentos
  useEffect(() => {
    const state = location.state;
    if (state?.equipamento) {
      const eq = state.equipamento;
      // Pré-seleciona cliente (empresa)
      if (eq.empresaId) {
        setEmpresaIdEfetivo(eq.empresaId);
      }
      // Pré-seleciona academia (setor)
      if (eq.setorId) {
        setSetorId(eq.setorId);
      }
      // Pré-seleciona equipamento (máquina)
      if (eq.id) {
        setMaquinaId(eq.id);
      }
    }
  }, [location.state]);


  useEffect(() => {
    async function carregarBase() {
      if (!usuarioLogado?.grupoId) return;

      const grupoId = usuarioLogado.grupoId;

      // Empresas
      setCarregandoEmpresas(true);
      try {
        const empresasRef = collection(db, "empresas");
        const qEmp = query(empresasRef, where("grupoId", "==", grupoId));
        const snapEmp = await getDocs(qEmp);
        const listaEmp = snapEmp.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        // Aplica escopo de empresas permitidas para evitar seleção inválida (principal causa de "permission-denied")
        const nivel = nivelUsuario(usuarioLogado);
        const podeVerTodas =
          usuarioLogado?.acessoGlobal === true ||
          usuarioLogado?.globalAccess === true ||
          nivel >= NIVEIS.ADMIN;

        const permitidas = Array.isArray(usuarioLogado?.empresasPermitidas)
          ? usuarioLogado.empresasPermitidas
          : [];
        const permitidasIds = permitidas.map((e) => e?.id).filter(Boolean);
        const permitidasNomes = permitidas
          .map((e) => String(e?.nome || "").trim())
          .filter(Boolean);

        let listaEmpFiltrada = listaEmp;
        if (!podeVerTodas) {
          if (permitidasIds.length) {
            listaEmpFiltrada = listaEmp.filter((e) => permitidasIds.includes(e.id));
          } else if (permitidasNomes.length) {
            listaEmpFiltrada = listaEmp.filter((e) => permitidasNomes.includes(String(e.nome || "").trim()));
          } else if (usuarioLogado?.empresa) {
            const n = String(usuarioLogado.empresa || "").trim();
            if (n) listaEmpFiltrada = listaEmp.filter((e) => String(e.nome || "").trim() === n);
          }
        }

        setEmpresas(listaEmpFiltrada);
        // Define empresa padrão para não deixar o form "travado" (tela preta por referência indefinida)
        if (!empresaIdEfetivo) {
          const preferida =
            usuarioLogado?.empresaId ||
            empresaAtual?.id ||
            (listaEmpFiltrada.length === 1 ? listaEmpFiltrada[0].id : "");
          if (preferida) setEmpresaIdEfetivo(preferida);
        }
      } catch (e) {
        console.error("Erro ao carregar empresas:", e);
        const code = e?.code || "";
        // Para perfis com leitura restrita (ex: Cliente), não travar a abertura da OS.
        if (code === "permission-denied") {
          const empId = usuarioLogado?.empresaId || empresaAtual?.id || "";
          const empNome = usuarioLogado?.empresaNome || empresaAtual?.nome || "Minha empresa";
          if (empId) {
            setEmpresas([{ id: empId, nome: empNome }]);
            setEmpresaIdEfetivo(empId);
          }
        } else {
          setErro("Erro ao carregar empresas. Verifique o cadastro do grupo.");
        }
      } finally {
        setCarregandoEmpresas(false);
      }

      // Setores
      setCarregandoSetores(true);
      try {
        const setoresRef = collection(db, "setores");
        const qSet = query(setoresRef, where("grupoId", "==", grupoId));
        const snapSet = await getDocs(qSet);
        const listaSet = snapSet.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setSetores(listaSet);
      } catch (e) {
        console.error("Erro ao carregar setores:", e);
        setErro((prev) =>
          prev
            ? prev + " / Erro ao carregar setores."
            : "Erro ao carregar setores."
        );
      } finally {
        setCarregandoSetores(false);
      }

      // Máquinas
      setCarregandoMaquinas(true);
      try {
        const maqRef = collection(db, "maquinas");
        const qMaq = query(maqRef, where("grupoId", "==", grupoId));
        const snapMaq = await getDocs(qMaq);
        const listaMaq = snapMaq.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setMaquinas(listaMaq);
      } catch (e) {
        console.error("Erro ao carregar máquinas:", e);
        setErro((prev) =>
          prev
            ? prev + " / Erro ao carregar máquinas."
            : "Erro ao carregar máquinas."
        );
      } finally {
        setCarregandoMaquinas(false);
      }

      // Status
      setCarregandoStatus(true);
      try {
        const statusRef = collection(db, "statusOs");
        const qStatus = query(statusRef, where("grupoId", "==", grupoId));
        const snapStatus = await getDocs(qStatus);
        const listaStatusDb = snapStatus.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        // Status de fábrica (padrão do sistema) - sempre disponíveis
        const statusFabrica = [
          {id:"fab_aberta", nome:"Aberta", cor:"#2563eb", padrao:true},
          {id:"fab_aberta_24h", nome:"Aberta 24H", cor:"#7c3aed", padrao:true},
          {id:"fab_em_atendimento", nome:"Em Atendimento", cor:"#0ea5e9", padrao:true},
          {id:"fab_finalizada", nome:"Finalizada", cor:"#16a34a", padrao:true},
          {id:"fab_cancelada", nome:"Cancelada", cor:"#dc2626", padrao:true},
          {id:"fab_aguard_mat", nome:"Aguardando Material", cor:"#ca8a04", padrao:true},
          {id:"fab_solic_mat", nome:"Solicitação de Material", cor:"#d97706", padrao:true},
          {id:"fab_aguard_parada", nome:"Aguardando Parada de Máquina", cor:"#64748b", padrao:true}
        ];

        // Combina status de fábrica + status do usuário
        const listaStatus = [...statusFabrica, ...listaStatusDb];
        setStatusList(listaStatus);

        // Seleciona "Aberta 24H" como padrão (se existir); senão, "Aberta"
        const padraoAberta24h =
          listaStatus.find((s) => s.id === "fab_aberta_24h") ||
          listaStatus.find((s) =>
            String(s.nome || "").toLowerCase().includes("aberta 24h")
          );
        const padraoAberta =
          listaStatus.find((s) => s.id === "fab_aberta") ||
          listaStatus.find(
            (s) => String(s.nome || "").toLowerCase() === "aberta"
          );

        if (padraoAberta24h) {
          setStatusId(padraoAberta24h.id);
        } else if (padraoAberta) {
          setStatusId(padraoAberta.id);
        } else if (listaStatus.length > 0) {
          setStatusId(listaStatus[0].id);
        }
      } catch (e) {
        console.error("Erro ao carregar status:", e);
        setErro((prev) =>
          prev
            ? prev + " / Erro ao carregar status."
            : "Erro ao carregar status."
        );
      } finally {
        setCarregandoStatus(false);
      }
    }

    carregarBase();
  }, [usuarioLogado]);

  useEffect(() => {
    async function carregarDefeitosDaMaquina() {
      // sempre limpa quando trocar de máquina ou usuário
      setDefeitos([]);
      setDefeitoId("");
      if (!usuarioLogado?.grupoId || !maquinaId) return;

      try {
        setCarregandoDefeitos(true);
        const defeitosRef = collection(db, "defeitos");
        const qDef = query(
          defeitosRef,
          where("grupoId", "==", usuarioLogado.grupoId),
          where("maquinasIds", "array-contains", maquinaId)
        );
        const snapDef = await getDocs(qDef);
        const listaDef = snapDef.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setDefeitos(listaDef);
      } catch (e) {
        console.error("Erro ao carregar defeitos da máquina:", e);
      } finally {
        setCarregandoDefeitos(false);
      }
    }

    carregarDefeitosDaMaquina();
  }, [usuarioLogado, maquinaId]);



  const setoresFiltrados = useMemo(() => {
    if (!empresaId) return setores;
    return setores.filter((s) => s.empresaId === empresaId);
  }, [setores, empresaId]);

  const maquinasFiltradas = useMemo(() => {
    const base = !setorId ? maquinas : maquinas.filter((m) => m.setorId === setorId);
    return [...base].sort((a, b) =>
      String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" })
    );
  }, [maquinas, setorId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuarioLogado?.grupoId) {
      setErro(
        "Usuário logado não possui grupo definido. Verifique o cadastro do Gestor."
      );
      return;
    }

    if (!empresaId) {
      setErro("Selecione a empresa.");
      return;
    }

    // Evita permission-denied: para usuários sem acesso global, a empresa da OS deve ser a empresa atual do usuário.
    if (!podeVerTodas && usuarioLogado?.empresaId && empresaId !== usuarioLogado.empresaId) {
      setErro("Sua conta não permite abrir OS em outra empresa sem mudar a empresa atual (Gerenciar → Mudar Cliente).");
      return;
    }
    if (!setorId) {
      setErro("Selecione o setor.");
      return;
    }
    if (!maquinaId) {
      setErro("Selecione a máquina.");
      return;
    }

    if (!defeitoId) {
      setErro("Selecione o defeito da OS.");
      return;
    }

    if (defeitoId === "__OUTROS__" && !defeitoCustom.trim()) {
      setErro("Descreva o defeito no campo 'Outro defeito'.");
      return;
    }

    const grupoId = usuarioLogado.grupoId;
    const grupoNome = usuarioLogado.grupoNome || usuarioLogado.grupo || null;

    const empresaIdEfetivo = (!podeVerTodas && usuarioLogado?.empresaId) ? usuarioLogado.empresaId : empresaId;
    const empresaSelecionada = empresas.find((e) => e.id === empresaIdEfetivo);
    const empresaNome = empresaSelecionada?.nome || empresaAtual?.nome || "(sem nome)";

    const setorSelecionado = setores.find((s) => s.id === setorId);
    const setorNome = setorSelecionado?.nome || "(sem nome)";

    const maquinaSelecionada = maquinas.find((m) => m.id === maquinaId);
    const maquinaNome = maquinaSelecionada?.nome || "(sem nome)";
    const maquinaChave = !!maquinaSelecionada?.maquinaChave;


    // Definição do status inicial da OS:
    // Usa o status selecionado no formulário (padrão: "Aberta 24H")
    let statusInicial = statusList.find((s) => s.id === statusId);

    // Se não encontrar, tenta "Aberta 24H" e depois "Aberta" como fallback
    if (!statusInicial) {
      statusInicial =
        statusList.find((s) => s.id === "fab_aberta_24h") ||
        statusList.find((s) =>
          String(s.nome || "").toLowerCase().includes("aberta 24h")
        ) ||
        statusList.find((s) => s.id === "fab_aberta") ||
        statusList.find(
          (s) => String(s.nome || "").toLowerCase() === "aberta"
        ) ||
        statusList[0];
    }

    if (!statusInicial) {
      alert("Não foi possível definir o status inicial da OS.");
      return;
    }

    const statusIdInicial = statusInicial.id;
    const statusNomeInicial = statusInicial.nome || null;
    const statusCorInicial = statusInicial.cor || null;


    const isDefeitoCustom = defeitoId === "__OUTROS__";
    const defeitoSelecionado = isDefeitoCustom ? null : defeitos.find((d) => d.id === defeitoId) || null;
    const tituloOs = isDefeitoCustom ? defeitoCustom.trim() : (defeitoSelecionado?.nome || "Defeito não especificado");

    setSalvando(true);

    try {
      const sequencial = await obterProximoCodigoSequencialOs(grupoId);
      const codigo = `OS${String(sequencial).padStart(4, "0")}`;

      const novaRef = doc(collection(db, "ordensServico"));
      const parouSim = String(maquinaParou || "Sim").toLowerCase().trim() === "sim";

      // Upload opcional de fotos na abertura (somente mobile; não aparece para Kiosk)
      let fotosAberturaUrls = [];
      if (podeAnexarFotoAbertura && fotosAbertura.length > 0) {
        setEnviandoFotos(true);
        try {
          for (let i = 0; i < fotosAbertura.length; i++) {
            const item = fotosAbertura[i];
            const file = item?.file;
            if (!file) continue;
            const url = await uploadToStorage({
              grupoId,
              empresaId: empresaIdEfetivo,
              pasta: "os_fotos",
              file,
              filenamePrefix: `${codigo}_abertura_${i + 1}`,
            });
            fotosAberturaUrls.push(url);
          }
        } finally {
          setEnviandoFotos(false);
        }
      }


      await setDoc(novaRef, {
        codigo,
        codigoSequencial: sequencial,

        titulo: tituloOs,
        defeitoId: isDefeitoCustom ? null : (defeitoSelecionado ? defeitoSelecionado.id : defeitoId || null),
        defeitoNome: tituloOs,
        defeitoCustom: isDefeitoCustom,
        descricao: descricao.trim() || null,
        criticidade: criticidade || "Média",
        maquinaParou: maquinaParou || "Sim",
        // ✅ Controle de parada (estado atual) — não depende do status da OS
        maquinaParadaAtiva: parouSim,
        maquinaParouEm: parouSim ? serverTimestamp() : null,
        maquinaVoltouRodarEm: null,
        tipoServico: tipoServico || "Corretiva",

        fotosAbertura: fotosAberturaUrls,
        fotosAberturaCount: fotosAberturaUrls.length,
        fotoAberturaUrl: fotosAberturaUrls[0] || null,


        grupoId,
        grupoNome: grupoNome || null,
        empresaId: empresaIdEfetivo,
        empresaNome,
        setorId,
        setorNome,
        maquinaId,
        maquinaNome,
        maquinaChave,

        statusId: statusIdInicial,
        statusNome: statusNomeInicial,
        statusCor: statusCorInicial,

        criadoPor: usuarioLogado?.uid || null,
        criadoPorNome: usuarioLogado?.nome || null,
        criadoEm: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSucesso(`OS criada com sucesso (código ${codigo}).`);
      limparFotosAbertura();
      setDefeitoId("");
      setDefeitoCustom("");
      setDefeitos([]);
      setTitulo("");
      setDescricao("");
      setCriticidade("Média");
      setMaquinaParou("Sim");
      setSetorId("");
      setMaquinaId("");
      // mantém empresa e status selecionados
    } catch (err) {
      console.error(err);
      const code = err?.code || "";
      if (code === "permission-denied") {
        setErro("Sem permissão para abrir OS para esta empresa. Verifique as empresas permitidas do usuário (ou acesso global).");
      } else {
        setErro("Erro ao abrir OS. Tente novamente.");
      }
    } finally {
      setSalvando(false);
    }
  };

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Abrir Nova OS"
      subtitulo=""
    >
      <section style={{ width: "100%" }}>
        <div
          className="pc-card"
          style={{
            width: "100%",
            paddingLeft: 40,
            paddingRight: 40,
            boxSizing: "border-box",
          }}
        >
          <header className="pc-table-header">
            <div className="pc-table-header-text">
              <h3>Abertura de Ordem de Serviço</h3>
              <p>
                Preencha os dados do equipamento e do problema para registrar uma
                nova OS.
              </p>
            </div>
          </header>

          {erro && (
            <div
              style={{
                marginTop: 12,
                padding: 8,
                borderRadius: 8,
                background: "#7f1d1d",
                color: "#fecaca",
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {sucesso && (
            <div
              style={{
                marginTop: 12,
                padding: 8,
                borderRadius: 8,
                background: "#14532d",
                color: "#bbf7d0",
                fontSize: 14,
              }}
            >
              {sucesso}
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            style={{
              marginTop: 12,
              maxWidth: 720,
              marginLeft: "auto",
              marginRight: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {/* Cliente */}
            <label>
              <span style={{ fontSize: 14 }}>Cliente</span>
              <select
                value={empresaId}
                onChange={(e) => {
                  setEmpresaIdEfetivo(e.target.value);
                  setSetorId("");
                  setMaquinaId("");
                }}
                disabled={carregandoEmpresas}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="">
                  {carregandoEmpresas
                    ? "Carregando clientes..."
                    : "Selecione o cliente"}
                </option>
                {empresas.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.nome || "(sem nome)"}
                  </option>
                ))}
              </select>
            </label>

            {/* Academia */}
            <label>
              <span style={{ fontSize: 14 }}>Academia</span>
              <select
                value={setorId}
                onChange={(e) => {
                  setSetorId(e.target.value);
                  setMaquinaId("");
                }}
                disabled={carregandoSetores || !empresaId}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="">
                  {carregandoSetores
                    ? "Carregando academias..."
                    : empresaId
                    ? "Selecione a academia"
                    : "Selecione o cliente primeiro"}
                </option>
                {setoresFiltrados.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome || "(sem nome)"}
                  </option>
                ))}
              </select>
            </label>

            {/* Equipamento */}
            <label>
              <span style={{ fontSize: 14 }}>Equipamento</span>
              <select
                value={maquinaId}
                onChange={(e) => {
                  setMaquinaId(e.target.value);
                }}
                disabled={carregandoMaquinas || !setorId}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="">
                  {carregandoMaquinas
                    ? "Carregando equipamentos..."
                    : setorId
                    ? "Selecione o equipamento"
                    : "Selecione a academia primeiro"}
                </option>
                {maquinasFiltradas.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nome || "(sem nome)"}
                  </option>
                ))}
              </select>
            </label>

{/* Defeito */}
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 14 }}>Defeito</span>
              <select
                value={defeitoId}
                onChange={(e) => {
                  setDefeitoId(e.target.value);
                  if (e.target.value !== "__OUTROS__") {
                    setDefeitoCustom("");
                  }
                }}
                disabled={!maquinaId || carregandoDefeitos}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                {!maquinaId ? (
                  <option value="">Selecione a máquina primeiro</option>
                ) : carregandoDefeitos ? (
                  <option value="">Carregando defeitos...</option>
                ) : (
                  <>
                    <option value="">Selecione o defeito</option>
                    {defeitos.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.nome || "(sem nome)"}
                      </option>
                    ))}
                    <option value="__OUTROS__">Outros (descrever manualmente)</option>
                  </>
                )}
              </select>
            </label>

            {/* Campo para defeito customizado */}
            {defeitoId === "__OUTROS__" && (
              <label>
                <span style={{ fontSize: 14 }}>Descreva o defeito</span>
                <input
                  type="text"
                  value={defeitoCustom}
                  onChange={(e) => setDefeitoCustom(e.target.value)}
                  placeholder="Ex: Barulho estranho no motor"
                  style={{
                    width: "100%",
                    padding: "9px 11px",
                                        borderRadius: 8,
                    border: "1px solid var(--cor-borda-input, #334155)",
                    background: "var(--cor-input-bg, transparent)",
                    color: "var(--cor-input-texto, inherit)",
                  }}
                />
              </label>
            )}

           {/* Criticidade */}
            <label>
              <span style={{ fontSize: 14 }}>Criticidade</span>
              <select
                value={criticidade}
                onChange={(e) => setCriticidade(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="Alta">Alta</option>
                <option value="Média">Média</option>
                <option value="Baixa">Baixa</option>
              </select>
            </label>

            {/* Tipo de Serviço */}
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 14 }}>Tipo de Serviço</span>
              <select
                value={tipoServico}
                onChange={(e) => setTipoServico(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="Corretiva">Corretiva</option>
                <option value="Melhoria">Melhoria</option>
                <option value="Instalação">Instalação</option>
              </select>
            </label>
            {/* Máquina parou por causa do defeito? */}
            <label>
              <span style={{ fontSize: 14 }}>Máquina parou por causa do defeito?</span>
              <select
                value={maquinaParou}
                onChange={(e) => setMaquinaParou(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                }}
              >
                <option value="Sim">Sim</option>
                <option value="Não">Não</option>
              </select>
            </label>


            {/* Descrição / Defeito */}
            <label>
              <span style={{ fontSize: 14 }}>Descrição / Defeito relatado</span>
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={4}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                                    borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                  resize: "vertical",
                }}
              />
            </label>

            {podeAnexarFotoAbertura ? (
              <label>
                <span style={{ fontSize: 14 }}>Foto (opcional)</span>

                <div
                  style={{
                    marginTop: 6,
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <button
                    type="button"
                    className="pc-btn-secundario"
                    disabled={salvando || enviandoFotos}
                    onClick={() => inputFotoCameraRef.current?.click()}
                  >
                    📷 Câmera
                  </button>

                  <button
                    type="button"
                    className="pc-btn-secundario"
                    disabled={salvando || enviandoFotos}
                    onClick={() => inputFotoArquivoRef.current?.click()}
                  >
                    ⬆️ Arquivo
                  </button>

                  {enviandoFotos ? (
                    <small style={{ fontSize: 12, opacity: 0.85 }}>
                      Enviando...
                    </small>
                  ) : null}
                </div>

                {/* Inputs escondidos */}
                <input
                  ref={inputFotoCameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    anexarFotosAbertura(e.target.files, "camera");
                    if (e.target) e.target.value = "";
                  }}
                />
                <input
                  ref={inputFotoArquivoRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    anexarFotosAbertura(e.target.files, "arquivo");
                    if (e.target) e.target.value = "";
                  }}
                />

                {/* Preview */}
                {fotosAbertura.length > 0 ? (
                  <div
                    style={{
                      marginTop: 10,
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    {fotosAbertura.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: 8,
                          borderRadius: 10,
                          border: "1px solid var(--cor-borda-input, #334155)",
                        }}
                      >
                        <img
                          src={item.previewUrl}
                          alt=""
                          style={{
                            width: 52,
                            height: 52,
                            objectFit: "cover",
                            borderRadius: 8,
                            border: "1px solid var(--cor-borda-input, #334155)",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          <a
                            href={item.previewUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: 12, opacity: 0.9 }}
                          >
                            Abrir
                          </a>
                          <button
                            type="button"
                            className="pc-btn-perigo"
                            style={{ padding: "4px 8px", fontSize: 12 }}
                            onClick={() => removerFotoAbertura(item.id)}
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <small style={{ fontSize: 12, opacity: 0.85, display: "block", marginTop: 8 }}>
                  As fotos serão anexadas no momento de abrir a OS.
                </small>
              </label>
            ) : null}

            {/* Ações */}
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/ordens-servico")}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="pc-btn-primario"
                disabled={
                  salvando ||
                  carregandoEmpresas ||
                  carregandoSetores ||
                  carregandoMaquinas
                }
              >
                {salvando ? "Salvando..." : "Abrir OS"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default NovaOS;
